import { FormbrewError, isFormbrewError } from "@formbrew/js";
import type { FormbrewFormController } from "@formbrew/js/dom";
import { dispatchFormbrewEvent } from "./events.ts";
import { render } from "./render.ts";

const ELEMENT_NAME = "formbrew-form";
const OBSERVED_ATTRIBUTES = ["base-url", "token"];

export class FormbrewFormElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return OBSERVED_ATTRIBUTES;
  }

  #abortController: AbortController | null = null;
  #controller: FormbrewFormController | null = null;
  #generation = 0;
  #mountScheduled = false;

  get controller(): FormbrewFormController | null {
    return this.#controller;
  }

  get state(): "error" | "loading" | "ready" | null {
    const state = this.dataset.formbrewState;

    if (state === "error" || state === "loading" || state === "ready") {
      return state;
    }

    return null;
  }

  connectedCallback(): void {
    this.classList.add("fb-embed");
    this.setAttribute("data-formbrew-embed", "");
    this.#scheduleMount();
  }

  disconnectedCallback(): void {
    this.#invalidateActiveRender();
    this.removeAttribute("data-formbrew-state");
    this.replaceChildren();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.isConnected || oldValue === newValue) {
      return;
    }

    this.#invalidateActiveRender();
    this.#scheduleMount();
  }

  #scheduleMount(): void {
    if (this.#mountScheduled) {
      return;
    }

    this.#mountScheduled = true;
    queueMicrotask(() => {
      this.#mountScheduled = false;

      if (!this.isConnected) {
        return;
      }

      void this.#mount();
    });
  }

  async #mount(): Promise<void> {
    this.#invalidateActiveRender();
    const generation = this.#generation;

    const token = this.getAttribute("token")?.trim();
    if (!token) {
      const error = new FormbrewError("Add a public token with the token attribute.", {
        code: "ConfigurationError",
      });

      this.#renderError(error);

      return;
    }

    const target = document.createElement("div");
    const abortController = new AbortController();
    const baseUrl = this.getAttribute("base-url")?.trim();
    this.#abortController = abortController;
    this.dataset.formbrewState = "loading";
    this.replaceChildren(target);

    try {
      const controller = await render(target, {
        baseUrl: baseUrl || undefined,
        onError: (error) => {
          if (!this.#isActive(generation)) {
            return;
          }

          dispatchFormbrewEvent(this, "formbrew:error", { error, phase: "submit" });
        },
        onSuccess: (result, mountedController) => {
          if (!this.#isActive(generation)) {
            return;
          }

          dispatchFormbrewEvent(this, "formbrew:success", {
            controller: mountedController,
            result,
          });
        },
        signal: abortController.signal,
        token,
      });

      if (!this.#isActive(generation)) {
        controller.destroy();

        return;
      }

      this.#controller = controller;
      this.dataset.formbrewState = "ready";
      dispatchFormbrewEvent(this, "formbrew:ready", {
        controller,
        definition: controller.definition,
      });
    } catch (error) {
      if (!this.#isActive(generation) || abortController.signal.aborted) {
        return;
      }

      const formbrewError = normalizeElementError(error);
      this.#renderError(formbrewError);
    }
  }

  #destroyActiveRender(): void {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#controller?.destroy();
    this.#controller = null;
  }

  #invalidateActiveRender(): void {
    this.#generation += 1;
    this.#destroyActiveRender();
  }

  #isActive(generation: number): boolean {
    return this.isConnected && this.#generation === generation;
  }

  #renderError(error: FormbrewError): void {
    const status = document.createElement("p");
    status.className = "fb-formbrew-error";
    status.role = "alert";
    status.textContent = error.message;
    this.dataset.formbrewState = "error";
    this.replaceChildren(status);
    dispatchFormbrewEvent(this, "formbrew:error", { error, phase: "load" });
  }
}

export const registerFormbrewFormElement = (): void => {
  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  customElements.define(ELEMENT_NAME, FormbrewFormElement);
};

const normalizeElementError = (error: unknown): FormbrewError => {
  if (isFormbrewError(error)) {
    return error;
  }

  let message = "Unable to load this form.";
  if (error instanceof Error) {
    message = error.message;
  }

  return new FormbrewError(message, {
    cause: error,
    code: "RendererError",
  });
};
